<template>
  <v-text-field
      v-model="displayValue"
      :label="label"
      :hint="hint"
      persistent-hint
      :error="!!error"
      :success="!error && modelValue !== ''"
      :messages="[error || successMessage].filter(Boolean)"
      @blur="formatAndEmit"
  />
</template>

<script setup lang="ts">
import { ref, watch, computed, defineProps, defineEmits } from 'vue';
import { parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js';

const props = defineProps<{
  modelValue: string;
  label?: string;
  hint?: string;
  country?: CountryCode;
  successMessage?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const country = computed(() => props.country || 'US');

const displayValue = ref(props.modelValue);
const error = ref<string | null>(null);
const successMessage = computed(() => props.successMessage ?? 'Valid phone number');

// When external modelValue changes, re-parse and set display value nicely
watch(
    () => props.modelValue,
    (newVal) => {
      if (!newVal) return;
      const parsed = parsePhoneNumberFromString(newVal);
      if (parsed?.isValid()) {
        displayValue.value = parsed.formatNational(); // e.g., (801) 555-1234
      } else {
        displayValue.value = newVal;
      }
    },
    { immediate: true }
);

// When user types in displayValue, we update modelValue on blur
function formatAndEmit() {
  const parsed = parsePhoneNumberFromString(displayValue.value, country.value);
  if (parsed?.isValid()) {
    displayValue.value = parsed.formatNational(); // keep input friendly
    emit('update:modelValue', parsed.number); // emit full E.164 value
    error.value = null;
  } else {
    error.value = 'Invalid phone number format';
  }
}
</script>
